/* eslint-disable lit/binding-positions */
import { deepCopy } from '@rxdi/graphql-client';
import { classMap, Component, css, html, LitElement, property, svg, TemplateResult } from '@rxdi/lit-html';

const Arrow = svg`<svg width="16" height="16" viewBox="0 0 16 16" class="MuiTableSortLabel-icon MuiTableSortLabel-iconDirectionAsc"><path fill="currentColor" d="M11 7H5l3-4z"></path></svg>`;
const DoubleArrow = svg`<svg width="16" height="16" viewBox="0 0 16 16" class="jss572"><path fill="currentColor" d="M11 7H5l3-4zM5 9h6l-3 4z"></path></svg>`;

export type Sort = 'asc' | 'desc' | 'none';
export interface LabelChangedEvent {
  title: string;
  sort: Sort;
  template?: TemplateResult;
}
interface State {
  titles: Record<string, { sort: Sort }>;
}

/**
 * @customElement rx-datatable
 */
@Component({
  selector: 'rx-datatable',
  style: css`
    :host() {
      display: block;

      --neutral200: #f7f5f9;
      --neutral300: #ece9f1;

      --accent600: #248da1;
    }

    caption {
      font-weight: 600;
      margin-bottom: 1rem;
      font-style: normal;
      text-align: left;
    }

    table {
      border-collapse: collapse;
      width: 100%;
      font-size: 0.8rem;
    }

    th {
      font-weight: 500;
      background: var(--neutral200, #fbfbfb);
      color: var(--accent600, #75777a);
    }

    th > .table-header-container {
      justify-content: space-between;
      display: flex;
    }

    svg {
      width: 1rem;
    }

    th,
    td {
      border-bottom: 1px solid var(--neutral300, #f4f4f4);
      border-top: 1px solid var(--neutral300, #f4f4f4);
      border-collapse: collapse;
      padding: 1rem;
      text-align: left;
    }

    .relative {
      position: relative;
    }

    .hover {
      user-select: none;
    }

    .hover:hover {
      color: #359ec0;
      cursor: pointer;
    }

    .rotateUp {
      transform: rotate(0deg);
    }

    .rotateDown {
      transform: rotate(180deg);
    }

    .sorting-arrows {
      transition: opacity 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, transform 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    }

    .pagination {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      padding: 10px;
      gap: 10px;
    }

    .pagination-btn {
      padding: 5px 10px;
      cursor: pointer;
      background: #fff;
      border: 1px solid #ccc;
      border-radius: 4px;
    }

    .pagination-btn:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  `,
  template(this: TableComponent) {
    return html`
      <style>
        .row:hover {
          background: whitesmoke;
          cursor: ${this.pointerOnHover ? 'pointer' : 'auto'};
        }
      </style>
      <table>
        ${this.caption
          ? html`
              <caption>
                ${this.caption}
              </caption>
            `
          : ''}
        ${this.disableHeader
          ? ''
          : html`
              <thead>
                <tr>
                  ${this.headers.map(
                    (header, index) =>
                      html`
                        <th class="hover" @click=${() => this.onHeaderColumnClicked(header)} scope="col" key=${index}>
                          <div style="display: flex">
                            <div>${header?.template || header?.title}</div>
                            <div class="relative">
                              ${header.title
                                ? html`
                                    ${this.state?.titles?.[header.title].sort === 'none'
                                      ? DoubleArrow
                                      : html` <div
                                          class=${classMap({
                                            'sorting-arrows': true,
                                            rotateUp: this.state?.titles?.[header?.title].sort === 'asc',
                                            rotateDown: this.state?.titles?.[header?.title].sort === 'desc',
                                          })}
                                        >
                                          ${Arrow}
                                        </div>`}
                                  `
                                : html``}
                            </div>
                          </div>
                        </th>
                      `
                  )}
                </tr>
              </thead>
            `}
        <tbody>
          ${(this.pageSize
            ? this.rows.slice(this.pageIndex * this.pageSize, (this.pageIndex + 1) * this.pageSize)
            : this.rows
          ).map(
            (row, index) => html`
              <tr
                @click=${() => {
                  row['index'] = index;

                  this.dispatchEvent(new CustomEvent('rowClicked', { detail: row }));
                }}
                class="row"
                key=${index}
              >
                ${row.map((cell, index) => html` <td part="td" key=${index}>${cell}</td> `)}
              </tr>
            `
          )}
        </tbody>
      </table>
      ${this.pageSize
        ? html`
            <div class="pagination">
              <button @click=${() => this.prevPage()} ?disabled=${this.pageIndex === 0} class="pagination-btn">
                Prev
              </button>
              <span> Page ${this.pageIndex + 1} of ${Math.ceil(this.rows.length / this.pageSize)} </span>
              <button
                @click=${() => this.nextPage()}
                ?disabled=${(this.pageIndex + 1) * this.pageSize >= this.rows.length}
                class="pagination-btn"
              >
                Next
              </button>
            </div>
          `
        : ''}
    `;
  },
})
export class TableComponent extends LitElement {
  @property({ type: String }) caption: string | TemplateResult = '';
  @property({ type: Boolean }) disableHeader: boolean;
  @property({ type: Boolean }) pointerOnHover: boolean;
  @property({ type: Boolean }) autosort: boolean;
  @property({ type: Array }) headers: {
    title: string;
    template?: TemplateResult;
  }[] = [];
  @property({ type: Array }) rows = [[]];
  @property({ type: Number }) pageSize: number;
  @property({ type: Number }) pageIndex = 0;

  @property({ type: Object })
  private state: State = {} as State;

  OnUpdateFirst() {
    this.state = {
      titles: this.setDefaultLabels(),
    };
  }
  setDefaultLabels() {
    return this.headers.reduce((prev, curr) => {
      prev[curr.title] = { sort: 'none' };
      return prev;
    }, {} as Record<string, { sort: Sort }>);
  }

  onHeaderColumnClicked(header: { title: string; template?: TemplateResult }) {
    const currentLabel: { sort: Sort } = {
      sort: this.state.titles[header.title].sort === 'asc' ? 'desc' : 'asc',
    };
    this.state.titles = this.setDefaultLabels();
    this.state.titles[header.title] = currentLabel;
    this.columnClicked({
      title: header.title,
      sort: currentLabel.sort,
      template: header.template,
    });
    this.setState(this.state);
  }

  private columnClicked(detail: LabelChangedEvent) {
    this.dispatchEvent(
      new CustomEvent<LabelChangedEvent>('columnClicked', {
        detail,
      })
    );
  }

  setState(state: State) {
    this.state = deepCopy(state);
  }

  getState() {
    return this.state;
  }

  private nextPage() {
    if ((this.pageIndex + 1) * this.pageSize < this.rows.length) {
      this.pageIndex++;
    }
  }

  private prevPage() {
    if (this.pageIndex > 0) {
      this.pageIndex--;
    }
  }
}
